import type {
  CookieDeclaration,
  PrivacyCategory,
  PrivacyMode,
  SiteAudience,
  SitePrivacyConfig,
} from "./types.js";

const modes = new Set<PrivacyMode>([
  "essential-only",
  "uk-statistics-opt-out",
  "prior-consent",
]);
const audiences = new Set<SiteAudience>(["uk", "global"]);
const categories = new Set<PrivacyCategory>([
  "essential",
  "statistics",
  "functionality",
]);
const identifier = /^[a-z0-9][a-z0-9-]{0,62}$/;
const hostname = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const cookieToken = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;
const topLevelKeys = new Set([
  "schemaVersion",
  "siteId",
  "audience",
  "mode",
  "policyVersion",
  "privacyPath",
  "preferenceLifetimeDays",
  "services",
]);
const serviceKeys = new Set([
  "id",
  "displayName",
  "provider",
  "category",
  "defaultState",
  "cookies",
  "storageKeys",
  "requestDomains",
  "load",
  "disable",
]);

function cookieName(cookie: unknown): string {
  if (typeof cookie === "string") return cookie;
  if (!cookie || typeof cookie !== "object") return "";
  const name = (cookie as Partial<CookieDeclaration>).name;
  return typeof name === "string" ? name : "";
}

export function validateConfig(config: SitePrivacyConfig): void {
  const errors: string[] = [];

  for (const key of Object.keys(config)) {
    if (!topLevelKeys.has(key)) errors.push(`unknown configuration field '${key}'`);
  }

  if (config.schemaVersion !== 1) errors.push("schemaVersion must be 1");
  if (!identifier.test(config.siteId)) errors.push("siteId is invalid");
  if (!audiences.has(config.audience)) errors.push("audience is unknown");
  if (!modes.has(config.mode)) errors.push("mode is unknown");
  if (!config.policyVersion || config.policyVersion.length > 64) {
    errors.push("policyVersion must contain 1-64 characters");
  }
  if (!/^\/(?!\/)/.test(config.privacyPath)) {
    errors.push("privacyPath must be a root-relative path, not a protocol-relative URL");
  }
  if (
    !Number.isInteger(config.preferenceLifetimeDays) ||
    config.preferenceLifetimeDays < 1 ||
    config.preferenceLifetimeDays > 365
  ) {
    errors.push("preferenceLifetimeDays must be an integer from 1 to 365");
  }
  if (config.audience === "global" && config.mode === "uk-statistics-opt-out") {
    errors.push("global sites cannot use uk-statistics-opt-out");
  }
  if (config.audience === "uk" && config.mode === "prior-consent") {
    // This is allowed: UK-only sites may deliberately choose the stricter mode.
  }

  if (!Array.isArray(config.services) || config.services.length > 32) {
    errors.push("services must be an array containing no more than 32 entries");
  }
  const ids = new Set<string>();
  const services = Array.isArray(config.services) ? config.services : [];
  for (const service of services) {
    for (const key of Object.keys(service)) {
      if (!serviceKeys.has(key)) errors.push(`service '${service.id}' has unknown field '${key}'`);
    }
    if (!identifier.test(service.id)) errors.push(`service id '${service.id}' is invalid`);
    if (ids.has(service.id)) errors.push(`service id '${service.id}' is duplicated`);
    ids.add(service.id);
    if (typeof service.displayName !== "string" || !service.displayName || service.displayName.length > 100) {
      errors.push(`service '${service.id}' has an invalid displayName`);
    }
    if (
      service.provider !== undefined &&
      (typeof service.provider !== "string" || !service.provider || service.provider.length > 100)
    ) {
      errors.push(`service '${service.id}' has an invalid provider`);
    }
    if (!categories.has(service.category)) {
      errors.push(`service '${service.id}' has an unknown category`);
    }
    if (service.defaultState !== "on" && service.defaultState !== "off") {
      errors.push(`service '${service.id}' has an unknown defaultState`);
    }
    if (service.category === "essential" && service.defaultState !== "on") {
      errors.push(`essential service '${service.id}' must default on`);
    }
    if (service.category !== "essential" && service.defaultState === "on") {
      const validUkStatistic =
        config.mode === "uk-statistics-opt-out" &&
        config.audience === "uk" &&
        service.category === "statistics";
      if (!validUkStatistic) {
        errors.push(`optional service '${service.id}' cannot default on in this mode`);
      }
    }
    if (config.mode === "essential-only" && service.category !== "essential") {
      errors.push(`essential-only mode cannot declare optional service '${service.id}'`);
    }
    if (typeof service.load !== "function") {
      errors.push(`service '${service.id}' must provide a load function`);
    }

    if (!Array.isArray(service.cookies)) {
      errors.push(`service '${service.id}' cookies must be an array`);
      continue;
    }
    const cookieNames = service.cookies.map(cookieName);
    if (cookieNames.some((name) => typeof name !== "string" || !cookieToken.test(name) || name.length > 100)) {
      errors.push(`service '${service.id}' has an invalid cookie declaration`);
    }
    for (const cookie of service.cookies) {
      if (typeof cookie === "string") continue;
      if (!cookie || typeof cookie !== "object") {
        errors.push(`service '${service.id}' has an invalid cookie declaration`);
        continue;
      }
      for (const key of Object.keys(cookie)) {
        if (!["name", "path", "domain"].includes(key)) {
          errors.push(`service '${service.id}' cookie '${cookie.name}' has unknown field '${key}'`);
        }
      }
      if (cookie.path !== undefined && !/^\/(?!\/)/.test(cookie.path)) {
        errors.push(`service '${service.id}' cookie '${cookie.name}' has an invalid path`);
      }
      const domain =
        typeof cookie.domain === "string" ? cookie.domain.replace(/^\./, "") : cookie.domain;
      if (domain !== undefined && (typeof domain !== "string" || !hostname.test(domain))) {
        errors.push(`service '${service.id}' cookie '${cookie.name}' has an invalid domain`);
      }
    }
    if (new Set(cookieNames).size !== cookieNames.length) {
      errors.push(`service '${service.id}' has duplicate cookie declarations`);
    }
    if (
      !Array.isArray(service.storageKeys) ||
      service.storageKeys.some(
        (key) => typeof key !== "string" || key.length === 0 || key.length > 100,
      )
    ) {
      errors.push(`service '${service.id}' has invalid storage keys`);
    } else if (new Set(service.storageKeys).size !== service.storageKeys.length) {
      errors.push(`service '${service.id}' has duplicate storage keys`);
    }
    if (!Array.isArray(service.requestDomains)) {
      errors.push(`service '${service.id}' requestDomains must be an array`);
      continue;
    }
    if (new Set(service.requestDomains).size !== service.requestDomains.length) {
      errors.push(`service '${service.id}' has duplicate request domains`);
    }
    for (const domain of service.requestDomains) {
      if (
        typeof domain !== "string" ||
        !hostname.test(domain) ||
        domain !== domain.toLowerCase()
      ) {
        errors.push(`service '${service.id}' has invalid request domain '${domain}'`);
      }
    }
  }

  if (errors.length > 0) {
    throw new Error(`Invalid privacy configuration:\n- ${errors.join("\n- ")}`);
  }
}
